import { useTranslation } from "@outschool/localization";
import { FilterDropdown } from "@outschool/ui-components-website";
import { useIsMobile } from "@outschool/ui-utils";
import { find } from "lodash";
import React from "react";

import { SearchFilters as SearchFiltersType } from "../../../../shared/generated/graphql";
import { useActivityString } from "../../../../shared/hooks/useActivityString";
import UncommonFilters from "../../search/UncommonFilters";
import { FiltersToShowType } from "./FiltersToShow";

export const CategoryMoreFilters = ({
  searchFilters,
  updateListings,
  closeFilterDropdown,
  openFilterDropdown,
  setOpenFilterDropdown,
  filtersToShow,
}: {
  searchFilters: SearchFiltersType;
  updateListings: (payload: SearchFiltersType) => void;
  closeFilterDropdown: () => void;
  openFilterDropdown: any;
  setOpenFilterDropdown: any;
  filtersToShow: FiltersToShowType;
}) => {
  const containerRef = React.useRef();
  const isMobile = useIsMobile();
  const { Delivery, Format } = useActivityString();
  const { t } = useTranslation(
    "client\\components\\categories\\CategoryMoreFilters",
  );

  const filtersToDisplay = searchFilters;

  const formats =
    filtersToShow.classType && filtersToDisplay.format
      ? filtersToDisplay.format.split(",")
      : [];
  const formatsSummary = formats
    .map(f => {
      const format = find(Format, ({ value }) => f === value);
      return format ? format.label : "";
    })
    .join(", ");

  const deliveries =
    filtersToShow.format && filtersToDisplay.delivery
      ? filtersToDisplay.delivery.split(",")
      : [];
  const deliveriesSummary = deliveries
    .map(f => {
      const delivery = find(Delivery, ({ value }) => f === value);
      return delivery ? delivery.label : "";
    })
    .join(", ");

  const enabledLanguageFilters =
    filtersToShow.language && filtersToDisplay.languageOfInstruction
      ? filtersToDisplay.languageOfInstruction.split(",")
      : [];

  const enabledCurriculumFilters = filtersToDisplay.curriculums
    ? filtersToDisplay.curriculums
    : [];

  const enabledStandardFilters = filtersToDisplay.standards
    ? filtersToDisplay.standards
    : [];

  const includeInProgressFixedLengthLiveFormatFilters =
    filtersToDisplay.includeInProgressFixedLengthLiveFormat ?? true;

  const uncommonFilterCount =
    formats.length +
    deliveries.length +
    enabledLanguageFilters.length +
    enabledCurriculumFilters.length +
    enabledStandardFilters.length;

  let uncommonFilterSummary = "";
  if (uncommonFilterCount) {
    if (formats.length && !deliveries.length) {
      uncommonFilterSummary = formatsSummary;
    } else if (!formats.length && deliveries.length) {
      uncommonFilterSummary =
        deliveriesSummary.length < 20
          ? deliveriesSummary
          : t("{{numLengths}} length", {
              numLengths: deliveries.length,
              count: deliveries.length,
              defaultValue_plural: "{{numLengths}} lengths",
            });
    } else {
      uncommonFilterSummary = t("+{{uncommonFilterCount}} filter", {
        uncommonFilterCount,
        count: uncommonFilterCount,
        defaultValue_plural: "+{{uncommonFilterCount}} filters",
      });
    }
  }

  if (
    filtersToShow.language ||
    filtersToShow.classType ||
    filtersToShow.format ||
    filtersToShow.curriculum ||
    filtersToShow.standards
  ) {
    return (
      <FilterDropdown
        filterName={t("More")}
        filterValue={uncommonFilterSummary}
        open={openFilterDropdown === "uncommon"}
        onClose={closeFilterDropdown}
        onOpen={() => setOpenFilterDropdown("uncommon")}
        referenceElement={isMobile ? containerRef.current : undefined}
        buttonSx={{
          padding: "1.1rem",
          "& > svg": {
            paddingTop: 0,
          },
        }}
      >
        <UncommonFilters
          value={{
            enabledFormatFilters: formats,
            enabledDeliveryFilters: deliveries,
            enabledLanguageFilters,
            enabledCurriculumFilters,
            enabledStandardFilters,
            includeInProgressFixedLengthLiveFormat:
              includeInProgressFixedLengthLiveFormatFilters,
          }}
          onChange={({
            enabledFormatFilters,
            enabledDeliveryFilters,
            enabledLanguageFilters,
            enabledCurriculumFilters = [],
            enabledStandardFilters = [],
            includeInProgressFixedLengthLiveFormat = true,
          }) => {
            const newFilterValues = {
              // @ts-ignore TS(2532): Object is possibly 'undefined'.
              format: enabledFormatFilters.length
                ? // @ts-ignore TS(2532): Object is possibly 'undefined'.
                  enabledFormatFilters.join(",")
                : null,
              // @ts-ignore TS(2532): Object is possibly 'undefined'.
              delivery: enabledDeliveryFilters.length
                ? // @ts-ignore TS(2532): Object is possibly 'undefined'.
                  enabledDeliveryFilters.join(",")
                : null,
              // @ts-ignore TS(2532): Object is possibly 'undefined'.
              languageOfInstruction: enabledLanguageFilters.length
                ? // @ts-ignore TS(2532): Object is possibly 'undefined'.
                  enabledLanguageFilters.join(",")
                : null,
              curriculums: enabledCurriculumFilters.length
                ? enabledCurriculumFilters
                : null,
              standards: enabledStandardFilters.length
                ? enabledStandardFilters
                : null,
              includeInProgressFixedLengthLiveFormat,
            };

            updateListings({ ...newFilterValues });
            closeFilterDropdown();
          }}
          onCancel={closeFilterDropdown}
          hideFormat={!filtersToShow.format}
          hideClassType={!filtersToShow.classType}
          hideLanguage={!filtersToShow.language}
        />
      </FilterDropdown>
    );
  } else {
    return null;
  }
};
